import { ActionIcon, InputLabel, Modal, Popover, Stack, Switch, Text, Button } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import type { Patient } from '@medplum/fhirtypes'
import { IconFilter } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { PatientSelect } from '@/pages/CallCenterHome/components/PatientSelect/PatientSelect'
import { formatPatientName } from '@/utils.ts'

export type ThreadsFilterOptions = {
  selectedPatient: Patient | null
  showUrgentOnly: boolean
  showFollowUpOnly: boolean
}

/**
 * Whether any filter is narrowing the list. Drives both the dot on the filter
 * trigger and the conversation list's empty-state copy, so the two can never
 * disagree about whether a filter is on. Lives here, beside the shape it reads,
 * rather than in a module of its own.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const hasActiveThreadsFilters = (options: ThreadsFilterOptions): boolean =>
  options.showUrgentOnly || options.showFollowUpOnly || options.selectedPatient !== null

type FilterControlsProps = {
  title?: string
  threadsFilterOptions: ThreadsFilterOptions
  onThreadsFilterOptionsChange: (value: ThreadsFilterOptions) => void
  /**
   * `modal` is the original centered dialog, still used by the minimized view.
   * `popover` anchors the same controls beside the conversation search field on
   * the redesigned Messages page.
   */
  variant?: 'modal' | 'popover'
}

export const FilterControls = ({
  title = 'Filters',
  threadsFilterOptions,
  onThreadsFilterOptionsChange,
  variant = 'modal',
}: FilterControlsProps) => {
  const [opened, { open, close }] = useDisclosure(false)
  const [showOnlyUrgent, setShowOnlyUrgent] = useState(threadsFilterOptions.showUrgentOnly)
  const [showOnlyFollowUp, setShowOnlyFollowUp] = useState(threadsFilterOptions.showFollowUpOnly)
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(
    threadsFilterOptions.selectedPatient,
  )

  useEffect(() => {
    if (opened) {
      return
    }

    setSelectedPatient(threadsFilterOptions.selectedPatient)
    setShowOnlyUrgent(threadsFilterOptions.showUrgentOnly)
    setShowOnlyFollowUp(threadsFilterOptions.showFollowUpOnly)
  }, [opened, threadsFilterOptions])

  const handlePatientChange = (patient: Patient | null): void => {
    setSelectedPatient(patient)
  }

  const handleApplyFilter = (): void => {
    onThreadsFilterOptionsChange({
      selectedPatient,
      showUrgentOnly: showOnlyUrgent,
      showFollowUpOnly: showOnlyFollowUp,
    })
    close()
  }

  const handleClearPatientFilter = (): void => {
    setSelectedPatient(null)
  }

  const handleClose = (): void => {
    setShowOnlyUrgent(threadsFilterOptions.showUrgentOnly)
    setShowOnlyFollowUp(threadsFilterOptions.showFollowUpOnly)
    setSelectedPatient(threadsFilterOptions.selectedPatient)
    close()
  }
  const isFilterDirty =
    showOnlyUrgent !== threadsFilterOptions.showUrgentOnly ||
    showOnlyFollowUp !== threadsFilterOptions.showFollowUpOnly ||
    selectedPatient?.id !== threadsFilterOptions.selectedPatient?.id

  const hasActiveFilters = hasActiveThreadsFilters(threadsFilterOptions)

  const renderTrigger = (onClick: () => void) => (
    <ActionIcon
      variant={variant === 'popover' ? 'default' : 'subtle'}
      color="gray"
      radius="md"
      size={variant === 'popover' ? 36 : 'lg'}
      onClick={onClick}
      aria-label="Open filters"
      aria-haspopup={variant === 'popover' ? 'dialog' : undefined}
      aria-expanded={variant === 'popover' ? opened : undefined}
    >
      <IconFilter size={18} />
      {hasActiveFilters && (
        <div className="absolute top-0 right-0 h-2 w-2 rounded-full bg-red-500" />
      )}
    </ActionIcon>
  )

  const body = (
    <Stack gap="md">
      <InputLabel>
        Patient:
        {selectedPatient ? (
          <Text>
            {`${formatPatientName(selectedPatient.name?.[0])}`}
            <Button variant="transparent" onClick={handleClearPatientFilter}>
              Clear{' '}
            </Button>
          </Text>
        ) : (
          <PatientSelect
            placeholder="Select related Patient"
            setSelectedPatient={handlePatientChange}
            hideAddPatientButton
            fullWidth
          />
        )}
      </InputLabel>
      <Switch
        label="Show tagged as Urgent"
        checked={showOnlyUrgent}
        onChange={(event) => setShowOnlyUrgent(event.currentTarget.checked)}
      />
      <Switch
        label="Show tagged as Follow Up"
        checked={showOnlyFollowUp}
        onChange={(event) => setShowOnlyFollowUp(event.currentTarget.checked)}
      />
      <Button disabled={!isFilterDirty} variant="outline" onClick={handleApplyFilter}>
        Apply
      </Button>
    </Stack>
  )

  if (variant === 'popover') {
    return (
      <Popover
        opened={opened}
        // Controlled mode: Mantine reports outside-click and Escape here, and
        // `handleClose` discards any unapplied edits exactly like the modal does.
        onChange={(next) => {
          if (!next) {
            handleClose()
          }
        }}
        position="bottom-end"
        radius="md"
        shadow="md"
        width={300}
        trapFocus
        withinPortal
      >
        <Popover.Target>{renderTrigger(() => (opened ? handleClose() : open()))}</Popover.Target>
        <Popover.Dropdown>{body}</Popover.Dropdown>
      </Popover>
    )
  }

  return (
    <>
      {renderTrigger(open)}
      <Modal opened={opened} onClose={handleClose} title={title} centered size="md">
        {body}
      </Modal>
    </>
  )
}
